import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead, {
  webPageSchema,
  breadcrumbSchema,
} from '@/components/feature/SEOHead';
import Breadcrumb, { getCategoryBreadcrumb } from '@/components/feature/Breadcrumb';
import { categories, getManhuaByCategory, getCategoryBySlug, ManhuaItem } from '@/mocks/manhua';

export default function Category() {
  const { categorySlug = 'all' } = useParams<{ categorySlug: string }>();
  const [sortBy, setSortBy] = useState<'hot' | 'rating' | 'new'>('hot');
  const [manhuaList, setManhuaList] = useState<ManhuaItem[]>([]);

  const category = getCategoryBySlug(categorySlug);
  const categoryName = category?.name || '全部';

  useEffect(() => {
    let list = getManhuaByCategory(categorySlug);
    if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'new') {
      list = [...list].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else {
      list = [...list].sort((a, b) => b.views - a.views);
    }
    setManhuaList(list);
  }, [categorySlug, sortBy]);

  const PAGE_TITLE = `${categoryName}韩国漫画 - 韩漫网`;
  const PAGE_DESC = `韩漫网${categoryName}分类汇集精选韩国漫画作品，免费在线阅读，下拉式设计流畅体验。`;
  const PAGE_KEYWORDS = `${categoryName},韩国漫画,韩漫网,免费阅读,在线观看`;

  const schemas = {
    ...webPageSchema({
      title: PAGE_TITLE,
      description: PAGE_DESC,
      url: `/category/${categorySlug}`,
    }),
    ...breadcrumbSchema(getCategoryBreadcrumb(categoryName).map((item) => ({
      name: item.label,
      url: item.to ? `http://kefaul.cn/${item.to}` : undefined,
    }))),
  };

  return (
    <>
      <SEOHead
        title={PAGE_TITLE}
        description={PAGE_DESC}
        keywords={PAGE_KEYWORDS}
        canonicalUrl={`/category/${categorySlug}`}
        schema={schemas}
      />

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
        <Breadcrumb items={getCategoryBreadcrumb(categoryName)} />

        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mt-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
              {categoryName}
              <br />
              <span className="text-2xl md:text-3xl font-normal text-foreground-400">
                {category?.description || '全部韩国漫画作品'}
              </span>
            </h1>
          </div>
          <p className="text-sm text-foreground-500">
            共 <strong className="text-primary-600">{manhuaList.length}</strong> 部作品
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-8">
          <div className="flex items-center bg-background-100 rounded-full px-1 py-1">
            {[
              { key: 'hot' as const, label: '最热' },
              { key: 'rating' as const, label: '评分最高' },
              { key: 'new' as const, label: '最新' },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setSortBy(s.key)}
                className={`px-4 py-1.5 text-xs font-medium rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  sortBy === s.key
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-1.5 ml-auto">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  categorySlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {manhuaList.length === 0 ? (
          <div className="text-center py-20">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-500">该分类下暂无漫画作品</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {manhuaList.map((manhua) => (
              <Link
                key={manhua.id}
                to={`/manhua/${manhua.slug}`}
                className="bg-background-100 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-all duration-300 group"
              >
                <div className="aspect-[3/4] overflow-hidden relative">
                  <img
                    src={manhua.cover}
                    alt={manhua.title}
                    title={`${manhua.title} - 韩漫网${categoryName}分类`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  {manhua.status === '连载中' && (
                    <span className="absolute top-2 right-2 px-2 py-0.5 bg-accent-500 text-background-50 text-[10px] font-medium rounded-full">
                      连载中
                    </span>
                  )}
                  {manhua.status === '已完结' && (
                    <span className="absolute top-2 right-2 px-2 py-0.5 bg-foreground-600 text-background-50 text-[10px] font-medium rounded-full">
                      已完结
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-semibold text-foreground-900 truncate">{manhua.title}</h3>
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center gap-0.5">
                      <i className="ri-star-fill text-[10px] text-amber-500"></i>
                      <span className="text-xs font-medium text-foreground-600">{manhua.rating}</span>
                    </div>
                    <span className="text-[10px] text-foreground-400">{manhua.author}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}